import React, { useState } from "react";
import { Link } from "react-router-dom";
import BirthInput from "./BirthInput";
import SavedKundlis from "./SavedKundlis";
import { useAuth } from "../../context/AuthContext";
import { Birth, runCalculator } from "../../utils/calculatorApi";
import "./VedicCalculator.css";
import "./KundliChart.css";

interface Planet {
  name: string;
  short: string;
  sign: string;
  house: number;
  degree: number;
  retrograde: boolean;
  combust: boolean;
  nakshatra: string;
  nakshatra_lord: string;
  nakshatra_pada: number;
}
/* The Vimshottari dasha, from the `dasha` calculator. The natal chart endpoint
   does not carry it, so it is a second call. Worth making: most free kundli
   tools draw the houses and stop, and the dasha is the part that answers "when",
   which is usually the question people actually arrived with. The page already
   promised it. */
interface DashaPeriod {
  lord: string;
  ends: string;
}
interface Dasha {
  current?: {
    mahadasha?: string;
    mahadasha_ends?: string;
    pratyantardasha?: string;
    pratyantardasha_ends?: string;
  };
  birth?: { dasha?: string; started?: string };
  timeline?: DashaPeriod[];
}

interface NatalChart {
  planets: Planet[];
  chart_svg?: string;
  navamsa_svg?: string;
  ayanamsa?: number;
  day_of_birth?: string;
}

/* The engine returns a finished North Indian chart as SVG, so there is nothing
   to draw here. It arrives as a full XML document, and the prolog has to come
   off before it can be injected into HTML. */
const cleanSvg = (svg?: string) =>
  svg ? svg.replace(/<\?xml[^>]*\?>/i, "").trim() : "";

/* What the chart returns, named on the page. These are the terms this page is
   trying to rank for, and listing what the tool actually produces is the honest
   way to have them present. */
const KUNDLI_PREVIEW = [
  { name: "Lagna", hint: "ascendant" },
  { name: "Rashi", hint: "moon sign" },
  { name: "Nakshatra", hint: "with pada" },
  { name: "12 houses", hint: "sign + lord" },
  { name: "9 grahas", hint: "by house" },
  { name: "Vimshottari dasha", hint: "with dates" },
];

const KUNDLI_DOSHAS = ["Mangal dosha", "Kaal Sarp", "Sade Sati"];

/* Lagna, rashi and nakshatra are all already in the payload: the ascendant and
   the Moon are two of the rows the engine returns. Reading them out is honest
   (nothing is invented) and it lets the panel fill in the way the matching
   page's score panel does, instead of staying a static list after a result. */
const findRow = (c: NatalChart | null, match: RegExp) =>
  c?.planets?.find((p) => match.test(p.name) || match.test(p.short));

const lagnaOf = (c: NatalChart | null) => findRow(c, /^(as|asc|ascendant|lagna)$/i)?.sign || null;
const moonOf = (c: NatalChart | null) => findRow(c, /^(mo|moon|chandra)$/i);

const KundliChart: React.FC = () => {
  const [busy, setBusy] = useState(false);
  const [chart, setChart] = useState<NatalChart | null>(null);
  const [dasha, setDasha] = useState<Dasha | null>(null);
  const [failed, setFailed] = useState("");
  const [tab, setTab] = useState<"d1" | "d9">("d1");
  /* Name and gender label the chart. The natal calculation does not use either
     (a birth chart is the same whoever it belongs to), so they are collected to
     title the result rather than to change it, which is the honest use. */
  const [name, setName] = useState("");
  const [gender, setGender] = useState<"Male" | "Female">("Male");
  const [savedOpen, setSavedOpen] = useState(false);
  const { user } = useAuth();

  const pickSaved = (birth: Birth, who: string) => {
    setName(who);
    setSavedOpen(false);
    run(birth);
  };

  const run = async (birth: Birth) => {
    setBusy(true);
    setFailed("");
    setChart(null);
    setDasha(null);
    try {
      /* Both at once. The dasha is a separate calculator, and a failure there
         must not cost the visitor their chart, so it is caught on its own. */
      const [natal, periods] = await Promise.all([
        runCalculator<NatalChart>("natal_chart", birth),
        runCalculator<Dasha>("dasha", birth).catch(() => null),
      ]);
      setDasha(periods);
      setChart(natal);
    } catch (e) {
      setFailed(
        e instanceof Error ? e.message : "Could not build your chart just now."
      );
    } finally {
      setBusy(false);
    }
  };

  const svg = cleanSvg(tab === "d1" ? chart?.chart_svg : chart?.navamsa_svg);

  return (
    <div className="vedcalc kchart">
      {/* Form on the left, a preview of what the chart returns on the right.
          Same two-panel shape as kundli matching, for the same reason: a lone
          centred form on a wide page reads as a widget dropped onto it. The
          panel also names lagna, rashi, nakshatra and dasha, which is what this
          page is trying to rank for, without stuffing them anywhere.

          alwaysSplitTime / offerUnknownTime: the native time picker can dismiss
          Meta's in-app browser outright, and this page takes ad traffic too. */}
      <div className="kchart-layout">
        <div className="kchart-card">
          <label className="birthinput-field kchart-name">
            <span>Name</span>
            <input
              type="text"
              value={name}
              placeholder="Your name"
              autoComplete="off"
              maxLength={40}
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          <div className="kchart-gender">
            <span className="kchart-gender__label">Gender</span>
            <div className="kchart-gender__track" data-on={gender} role="group" aria-label="Gender">
              <span className="kchart-gender__thumb" aria-hidden="true" />
              {(["Male", "Female"] as const).map((g) => (
                <button
                  key={g}
                  type="button"
                  className={"kchart-gender__btn" + (gender === g ? " is-on" : "")}
                  aria-pressed={gender === g}
                  onClick={() => setGender(g)}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          <BirthInput
            onSubmit={run}
            busy={busy}
            cta="Build my kundli"
            alwaysSplitTime
            offerUnknownTime
          />

          {user ? (
            <div className="kchart-saved-m">
              <button
                type="button"
                className="kchart-saved-mobile"
                aria-expanded={savedOpen}
                onClick={() => setSavedOpen((v) => !v)}
              >
                Saved kundlis
                <span
                  className={"kchart-saved-caret" + (savedOpen ? " is-open" : "")}
                  aria-hidden="true"
                />
              </button>
              {savedOpen ? (
                <div className="kchart-saved-menu kchart-saved-menu--m">
                  <SavedKundlis onPick={pickSaved} />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <aside
          className={"kchart-preview" + (chart ? " is-filled" : "")}
          aria-label={chart ? "Your chart summary" : "What your kundli will show"}
        >
          <p className="kchart-preview__eyebrow">
            {chart ? (name.trim() ? `${name.trim()}'s chart` : "Your chart") : "Your kundli will show"}
          </p>
          <ul className="kchart-preview__list">
            {chart ? (
              <>
                <li>
                  <span>Lagna</span>
                  <span className="kchart-preview__val">{lagnaOf(chart) || "\u2014"}</span>
                </li>
                <li>
                  <span>Rashi</span>
                  <span className="kchart-preview__val">{moonOf(chart)?.sign || "\u2014"}</span>
                </li>
                <li>
                  <span>Nakshatra</span>
                  <span className="kchart-preview__val">
                    {moonOf(chart)?.nakshatra || "\u2014"}
                    {moonOf(chart)?.nakshatra_pada ? (
                      <span className="kchart-preview__pada">
                        {" "}pada {moonOf(chart)?.nakshatra_pada}
                      </span>
                    ) : null}
                  </span>
                </li>
                {dasha?.current?.mahadasha ? (
                  <li>
                    <span>Mahadasha</span>
                    <span className="kchart-preview__val">
                      {dasha.current.mahadasha}
                      {dasha.current.mahadasha_ends ? (
                        <span className="kchart-preview__pada">
                          {" "}to {dasha.current.mahadasha_ends.replace(/^\w{3} /, "")}
                        </span>
                      ) : null}
                    </span>
                  </li>
                ) : null}
                <li>
                  <span>Grahas placed</span>
                  <span className="kchart-preview__val">{chart.planets?.length ?? 0}</span>
                </li>
                {chart.day_of_birth ? (
                  <li>
                    <span>Day of birth</span>
                    <span className="kchart-preview__val">{chart.day_of_birth}</span>
                  </li>
                ) : null}
              </>
            ) : (
              KUNDLI_PREVIEW.map((k) => (
                <li key={k.name}>
                  <span>{k.name}</span>
                  <span className="kchart-preview__hint">{k.hint}</span>
                </li>
              ))
            )}
          </ul>
          <p className="kchart-preview__eyebrow kchart-preview__eyebrow--sub">
            Also checked
          </p>
          <ul className="kchart-preview__list kchart-preview__list--dosha">
            {KUNDLI_DOSHAS.map((d) => (
              <li key={d}>
                <span>{d}</span>
                <span className="kchart-preview__hint">both</span>
              </li>
            ))}
          </ul>

          {user ? (
            <div className="kchart-savedwrap">
              <button
                type="button"
                className="kchart-saved-link"
                aria-expanded={savedOpen}
                onClick={() => setSavedOpen((v) => !v)}
              >
                Saved kundlis
                <span
                  className={"kchart-saved-caret" + (savedOpen ? " is-open" : "")}
                  aria-hidden="true"
                />
              </button>
              {savedOpen ? (
                <div className="kchart-saved-menu">
                  <SavedKundlis onPick={pickSaved} />
                </div>
              ) : null}
            </div>
          ) : null}
        </aside>
      </div>

      {failed ? (
        <div className="vedcalc-result kchart-result vedcalc-failed" role="status">
          <p>{failed}</p>
          <p className="vedcalc-note">
            Check the date, time and place and try again. If it keeps failing,
            ask Aura AI and it will read the chart for you.
          </p>
        </div>
      ) : null}

      {chart ? (
        <div className="vedcalc-result kchart-result" role="status">
          <p className="vedcalc-verdict">
            {name.trim() ? `${name.trim()}'s Vedic birth chart` : "Your Vedic birth chart"}
          </p>

          {chart.chart_svg ? (
            <>
              <div className="kundli-tabs" role="tablist">
                <button
                  role="tab"
                  aria-selected={tab === "d1"}
                  className={tab === "d1" ? "is-on" : ""}
                  onClick={() => setTab("d1")}
                >
                  Lagna chart (D1)
                </button>
                {chart.navamsa_svg ? (
                  <button
                    role="tab"
                    aria-selected={tab === "d9"}
                    className={tab === "d9" ? "is-on" : ""}
                    onClick={() => setTab("d9")}
                  >
                    Navamsa (D9)
                  </button>
                ) : null}
              </div>
              <div
                className="kundli-svg"
                /* The SVG is generated by our own chart engine, not user input. */
                dangerouslySetInnerHTML={{ __html: svg }}
              />
            </>
          ) : null}

          {/* THE DASHA TIMELINE.
              Srimandir devotes a whole section to why dasha analysis matters
              and then does not show one; most free kundli tools draw the houses
              and stop. It is the part that answers "when", and it is the reason
              someone comes back to the page in six months. */}
          {dasha?.timeline?.length ? (
            <div className="kundli-dasha">
              <h4 className="kundli-dasha__h">Your Vimshottari dasha timeline</h4>
              {dasha.current?.mahadasha ? (
                <p className="kundli-dasha__now">
                  Running now: <strong>{dasha.current.mahadasha}</strong>
                  {dasha.current.mahadasha_ends
                    ? ` until ${dasha.current.mahadasha_ends}`
                    : null}
                  {dasha.current.pratyantardasha ? (
                    <>
                      {" "}&middot; sub-period{" "}
                      <strong>{dasha.current.pratyantardasha}</strong>
                      {dasha.current.pratyantardasha_ends
                        ? ` until ${dasha.current.pratyantardasha_ends}`
                        : null}
                    </>
                  ) : null}
                </p>
              ) : null}
              <ol className="kundli-dasha__list">
                {dasha.timeline.map((d, i) => {
                  const ended =
                    !!d.ends && new Date(d.ends).getTime() < Date.now();
                  const isNow =
                    !!dasha.current?.mahadasha &&
                    dasha.current.mahadasha.split("/")[0] === d.lord &&
                    !ended;
                  return (
                    <li
                      key={`${d.lord}-${i}`}
                      className={
                        "kundli-dasha__row" +
                        (ended ? " is-past" : "") +
                        (isNow ? " is-now" : "")
                      }
                    >
                      <span className="kundli-dasha__lord">{d.lord}</span>
                      <span className="kundli-dasha__ends">
                        {ended ? "ended " : "until "}
                        {d.ends}
                      </span>
                    </li>
                  );
                })}
              </ol>
              <p className="kundli-dasha__note">
                Vimshottari runs 120 years across the nine grahas, starting from
                the nakshatra your Moon occupied at birth. The period you are in
                is what decides whether a placement in your kundli is active now
                or waiting.
              </p>
            </div>
          ) : null}

          {chart.planets?.length ? (
            <div className="kundli-dasha kundli-planets">
              <h4 className="kundli-dasha__h">Planet positions in your kundli</h4>
              <div className="kundli-table-wrap">
              <table className="kundli-table">
                <thead>
                  <tr>
                    <th>Planet</th>
                    <th>Sign</th>
                    <th>House</th>
                    <th>Degree</th>
                    <th>Nakshatra</th>
                  </tr>
                </thead>
                <tbody>
                  {chart.planets.map((p) => (
                    <tr key={p.name}>
                      <td>
                        {p.name}
                        {p.retrograde ? (
                          <span className="kundli-flag" title="Retrograde">
                            R
                          </span>
                        ) : null}
                        {p.combust ? (
                          <span className="kundli-flag" title="Combust">
                            C
                          </span>
                        ) : null}
                      </td>
                      <td>{p.sign}</td>
                      <td>{p.house}</td>
                      <td>{p.degree?.toFixed(2)}&deg;</td>
                      <td>
                        {p.nakshatra}
                        {p.nakshatra_pada ? ` (${p.nakshatra_pada})` : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              <p className="kundli-dasha__note">
                {chart.day_of_birth
                  ? `You were born on a ${chart.day_of_birth}. `
                  : ""}
                R marks a retrograde planet and C marks one combust, too close to
                the Sun to act freely. The chart is drawn in the North Indian
                style, where the houses stay fixed and the signs move.
              </p>
            </div>
          ) : null}

          <Link to="/chat" className="vedcalc-cta">
            Ask Aura AI to read this chart
          </Link>
        </div>
      ) : null}
    </div>
  );
};

export default KundliChart;
